import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { fmtMoney, fmtDate, fmtDateTime, Badge } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { StatCard } from "../components/StatCard";
import { DataTable } from "../components/DataTable";
import { Modal, Field, inputCls, selectCls, btnPrimary, btnSecondary } from "../components/Modal";
import { toast } from "sonner";
import {
  Store, Users, TrendingUp, ShieldCheck, LogOut, PauseCircle, PlayCircle, Plus, Eye, Pencil, Ban, Activity,
  Trash2, RotateCcw, AlertTriangle, KeyRound, Download, FileUp, Copy,
} from "lucide-react";

const STATUS_META = {
  active: { label: "ACTIVE", cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300" },
  suspended: { label: "SUSPENDUE", cls: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300" },
  disabled: { label: "DÉSACTIVÉE", cls: "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300" },
  deleted: { label: "SUPPRIMÉE", cls: "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300" },
};

const EMPTY_STORE = { store_name: "", owner_name: "", email: "", password: "", phone: "", address: "", city: "", country: "", currency: "FCFA", status: "active" };

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_STORE);
  const [editFor, setEditFor] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [deletedTenants, setDeletedTenants] = useState([]);
  const [deleteFor, setDeleteFor] = useState(null);
  const [deleteName, setDeleteName] = useState("");
  const [purgeFor, setPurgeFor] = useState(null);
  const [purgeName, setPurgeName] = useState("");
  const [licenses, setLicenses] = useState([]);
  const [licTenant, setLicTenant] = useState("");
  const [licResult, setLicResult] = useState(null);
  const [syncMode, setSyncMode] = useState("replace");

  const load = () => {
    api.get("/admin/overview").then((r) => setOverview(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/admin/tenants").then((r) => setTenants(r.data)).catch(() => {});
    api.get("/admin/tenants-deleted").then((r) => setDeletedTenants(r.data)).catch(() => {});
    api.get("/admin/licenses").then((r) => setLicenses(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (t, status) => {
    try {
      await api.patch(`/admin/tenants/${t.id}`, { status });
      toast.success(`${t.name} → ${STATUS_META[status].label}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const createStore = async () => {
    setBusy(true);
    try {
      await api.post("/admin/tenants", createForm);
      toast.success(`Quincaillerie « ${createForm.store_name} » créée avec son compte administrateur`);
      setCreateOpen(false);
      setCreateForm(EMPTY_STORE);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const openEdit = (t) => {
    setEditFor(t);
    setEditForm({ name: t.name, owner_name: t.owner_name || "", phone: t.phone || "", email: t.email || "",
      address: t.address || "", city: t.city || "", country: t.country || "", currency: t.currency || "FCFA" });
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/tenants/${editFor.id}`, editForm);
      toast.success("Informations mises à jour");
      setEditFor(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const openDetail = async (t) => {
    try {
      const { data } = await api.get(`/admin/tenants/${t.id}/detail`);
      setDetail(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const doLogout = async () => { await logout(); navigate("/login"); };

  const importQreq = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const payload = JSON.parse(await f.text());
      const { data } = await api.post("/admin/licenses/import-request", payload);
      toast.success(`Installation enregistrée : ${data.name} — ${data.installation_id}`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    e.target.value = "";
  };

  const genLicense = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/licenses/generate", { tenant_id: licTenant });
      setLicResult(data);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const downloadQlic = (payload, signature) => {
    const blob = new Blob([JSON.stringify({ payload, signature }, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "license.qlic";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const revokeLicense = async (l) => {
    if (!window.confirm(`Révoquer la licence de « ${l.hardware_store_name} » ? Elle ne pourra plus être renouvelée.`)) return;
    try {
      await api.post(`/admin/licenses/${l.license_id}/revoke`);
      toast.success("Licence révoquée");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const importSnapshot = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!licTenant) { toast.error("Sélectionnez d'abord une quincaillerie cible"); return; }
    if (!window.confirm(syncMode === "replace"
      ? "Cette opération REMPLACE toutes les données de la quincaillerie cible par celles du snapshot. Continuer ?"
      : "Fusionner les nouveautés du snapshot dans la quincaillerie cible (les suppressions locales ne sont pas propagées). Continuer ?")) return;
    setBusy(true);
    try {
      const payload = JSON.parse(await f.text());
      const { data } = await api.post(`/admin/sync/import/${licTenant}?mode=${syncMode}`, payload);
      const total = Object.values(data.imported || {}).reduce((a, b) => a + b, 0);
      toast.success(`Snapshot ${syncMode === "merge" ? "fusionné" : "restauré"} — ${total} documents importés`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  const openDelete = (t) => { setDeleteFor(t); setDeleteName(""); };
  const openPurge = (t) => { setPurgeFor(t); setPurgeName(""); };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/tenants/${deleteFor.id}`, { data: { confirm_name: deleteName } });
      toast.success(`« ${deleteFor.name} » déplacée dans les quincailleries supprimées`);
      setDeleteFor(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const restoreTenant = async (t) => {
    try {
      await api.post(`/admin/tenants/${t.id}/restore`);
      toast.success(`« ${t.name} » restaurée`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const confirmPurge = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/tenants/${purgeFor.id}/permanent`, { data: { confirm_name: purgeName } });
      toast.success(`« ${purgeFor.name} » et toutes ses données ont été définitivement supprimées`);
      setPurgeFor(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const columns = [
    { key: "name", label: "Quincaillerie", render: (t) => (
      <div><div className="font-semibold">{t.name}</div><div className="text-xs text-muted-foreground">{t.city || "—"} · {t.email}</div></div>) },
    { key: "owner_name", label: "Responsable" },
    { key: "created_at", label: "Créée le", render: (t) => fmtDate(t.created_at) },
    { key: "users_count", label: "Utilisateurs", className: "text-right" },
    { key: "products_count", label: "Produits", className: "text-right" },
    { key: "sales_count", label: "Ventes", className: "text-right" },
    { key: "status", label: "Statut", render: (t) => {
      const s = STATUS_META[t.status] || STATUS_META.active;
      return <span data-testid={`status-${t.id}`} className={`px-2 py-0.5 rounded-full text-xs font-bold ${s.cls}`}>{s.label}</span>; } },
    { key: "actions", label: "", className: "text-right", render: (t) => (
      <div className="flex justify-end gap-1 flex-wrap">
        <button data-testid={`detail-${t.id}`} onClick={() => openDetail(t)} title="Détails" className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-muted"><Eye className="h-4 w-4" /></button>
        <button data-testid={`edit-${t.id}`} onClick={() => openEdit(t)} title="Modifier" className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-muted"><Pencil className="h-4 w-4" /></button>
        {t.status !== "active" && (
          <button data-testid={`activate-${t.id}`} onClick={() => setStatus(t, "active")} title="Activer / Réactiver" className="h-8 px-2 inline-flex items-center gap-1 rounded-md text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950 text-xs font-bold"><PlayCircle className="h-4 w-4" /> Activer</button>)}
        {t.status === "active" && (
          <button data-testid={`suspend-${t.id}`} onClick={() => setStatus(t, "suspended")} title="Suspendre" className="h-8 px-2 inline-flex items-center gap-1 rounded-md text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950 text-xs font-bold"><PauseCircle className="h-4 w-4" /> Suspendre</button>)}
        {t.status !== "disabled" && (
          <button data-testid={`disable-${t.id}`} onClick={() => setStatus(t, "disabled")} title="Désactiver l'accès" className="h-8 px-2 inline-flex items-center gap-1 rounded-md text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950 text-xs font-bold"><Ban className="h-4 w-4" /> Désactiver</button>)}
        <button data-testid={`delete-${t.id}`} onClick={() => openDelete(t)} title="Supprimer la quincaillerie" className="h-8 w-8 inline-flex items-center justify-center rounded-md text-rose-700 hover:bg-rose-100 dark:hover:bg-rose-950"><Trash2 className="h-4 w-4" /></button>
      </div>) },
  ];

  const setC = (k) => (e) => setCreateForm({ ...createForm, [k]: e.target.value });
  const setE = (k) => (e) => setEditForm({ ...editForm, [k]: e.target.value });

  return (
    <div className="min-h-screen bg-background" data-testid="admin-page">
      <header className="h-16 bg-card border-b flex items-center gap-3 px-4 sm:px-6">
        <div className="h-9 w-9 rounded-lg bg-slate-900 dark:bg-slate-100 flex items-center justify-center">
          <ShieldCheck className="h-5 w-5 text-white dark:text-slate-900" />
        </div>
        <div>
          <div className="font-heading font-bold">Super Administrateur — Plateforme SaaS</div>
          <div className="text-xs text-muted-foreground">{user?.email}</div>
        </div>
        <div className="flex-1" />
        <button data-testid="create-store-btn" onClick={() => setCreateOpen(true)} className={btnPrimary}>
          <Plus className="h-4 w-4" /> Créer une quincaillerie
        </button>
        <button data-testid="admin-logout" onClick={doLogout} className={btnSecondary}><LogOut className="h-4 w-4" /> Déconnexion</button>
      </header>
      <main className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6">
        {overview && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard testid="admin-tenants" label="Quincailleries" value={overview.tenants_count} icon={Store} tone="blue" sub={`${overview.active_tenants} actives / ${overview.suspended_tenants} suspendues`} />
            <StatCard testid="admin-users" label="Comptes utilisateurs" value={overview.users_count} icon={Users} tone="emerald" />
            <StatCard testid="admin-sales" label="CA total plateforme" value={fmtMoney(overview.platform_sales_total)} icon={TrendingUp} tone="amber" />
            <StatCard testid="admin-disabled" label="Désactivées" value={tenants.filter((t) => t.status === "disabled").length} icon={Ban} tone="rose" />
          </div>
        )}
        <h1 className="font-heading text-xl font-bold">Quincailleries de la plateforme</h1>
        <DataTable testid="admin-tenants-table" columns={columns} rows={tenants} searchKeys={["name", "email", "city", "owner_name"]} pageSize={15} />

        <div className="pt-4" data-testid="deleted-section">
          <h2 className="font-heading text-lg font-bold flex items-center gap-2 text-slate-700 dark:text-slate-300">
            <Trash2 className="h-5 w-5" /> Quincailleries supprimées
            <span className="text-sm font-normal text-muted-foreground">({deletedTenants.length})</span>
          </h2>
          <p className="text-xs text-muted-foreground mt-1">Données conservées temporairement : restauration possible, ou suppression définitive irréversible.</p>
          {deletedTenants.length === 0 ? (
            <div className="mt-3 border border-dashed rounded-lg p-6 text-center text-sm text-muted-foreground" data-testid="deleted-empty">Aucune quincaillerie supprimée</div>
          ) : (
            <div className="mt-3 border rounded-lg divide-y" data-testid="deleted-list">
              {deletedTenants.map((t) => (
                <div key={t.id} className="flex items-center gap-3 px-4 py-3 flex-wrap" data-testid={`deleted-row-${t.id}`}>
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-semibold">{t.name}</div>
                    <div className="text-xs text-muted-foreground">
                      Supprimée le {fmtDateTime(t.deleted_at)}{t.deleted_by?.email ? ` par ${t.deleted_by.email}` : ""} · {t.users_count} utilisateurs · {t.sales_count} ventes
                    </div>
                  </div>
                  <button data-testid={`restore-${t.id}`} onClick={() => restoreTenant(t)}
                    className="h-8 px-3 inline-flex items-center gap-1 rounded-md text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950 text-xs font-bold border border-emerald-200 dark:border-emerald-900">
                    <RotateCcw className="h-4 w-4" /> Restaurer</button>
                  <button data-testid={`purge-${t.id}`} onClick={() => openPurge(t)}
                    className="h-8 px-3 inline-flex items-center gap-1 rounded-md text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950 text-xs font-bold border border-rose-200 dark:border-rose-900">
                    <Trash2 className="h-4 w-4" /> Supprimer définitivement</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="pt-4" data-testid="licenses-section">
          <h2 className="font-heading text-lg font-bold flex items-center gap-2 text-slate-700 dark:text-slate-300">
            <KeyRound className="h-5 w-5" /> Éditions Locales / Licences
            <span className="text-sm font-normal text-muted-foreground">({licenses.length})</span>
          </h2>
          <p className="text-xs text-muted-foreground mt-1">Licences annuelles signées Ed25519 pour les installations hors-ligne. La clé privée ne quitte jamais ce serveur.</p>
          <div className="flex gap-2 flex-wrap items-center mt-3">
            <label className={`${btnSecondary} cursor-pointer`} data-testid="import-qreq-label">
              <FileUp className="h-4 w-4" /> Importer une demande (.qreq)
              <input data-testid="import-qreq" type="file" accept=".qreq,application/json" className="hidden" onChange={importQreq} />
            </label>
            <select data-testid="license-tenant" value={licTenant} onChange={(e) => setLicTenant(e.target.value)} className={selectCls}>
              <option value="">— Quincaillerie —</option>
              {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <button data-testid="generate-license" onClick={genLicense} disabled={busy || !licTenant} className={btnPrimary}>Générer une licence (1 an)</button>
            <select data-testid="sync-mode" value={syncMode} onChange={(e) => setSyncMode(e.target.value)} className={selectCls}>
              <option value="replace">Remplacer toutes les données</option>
              <option value="merge">Fusionner (incrémental)</option>
            </select>
            <label className={`${btnSecondary} cursor-pointer`} data-testid="import-snapshot-label">
              <FileUp className="h-4 w-4" /> Restaurer un snapshot Local → Cloud (.json)
              <input data-testid="import-snapshot" type="file" accept=".json,application/json" className="hidden" onChange={importSnapshot} disabled={busy} />
            </label>
          </div>
          <p className="text-xs text-muted-foreground mt-1">La restauration remplace les données de la quincaillerie sélectionnée par le snapshot exporté depuis l'Édition Locale (Paramètres → Sauvegardes → Exporter JSON).</p>
          {licenses.length > 0 && (
            <div className="mt-3 border rounded-lg divide-y" data-testid="licenses-list">
              {licenses.map((l) => (
                <div key={l.license_id} className="flex items-center gap-3 px-4 py-3 flex-wrap" data-testid={`license-row-${l.license_id}`}>
                  <div className="flex-1 min-w-[240px]">
                    <div className="font-semibold">{l.hardware_store_name}</div>
                    <div className="text-xs text-muted-foreground font-mono">{l.installation_id} · émise le {fmtDate(l.issued_at)} · expire le {fmtDate(l.expires_at)}</div>
                  </div>
                  <span data-testid={`license-status-${l.license_id}`} className={`px-2 py-0.5 rounded-full text-xs font-bold ${l.status === "ACTIVE" ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300" : l.status === "REVOKED" ? "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300" : "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"}`}>{l.status}</span>
                  <button data-testid={`license-download-${l.license_id}`} onClick={() => downloadQlic({ schema: l.schema, license_id: l.license_id, hardware_store_id: l.hardware_store_id, hardware_store_name: l.hardware_store_name, installation_id: l.installation_id, issued_at: l.issued_at, expires_at: l.expires_at, license_type: l.license_type }, l.signature)} className="h-8 px-2 inline-flex items-center gap-1 rounded-md text-primary hover:bg-muted text-xs font-bold"><Download className="h-4 w-4" /> .qlic</button>
                  {l.status === "ACTIVE" && (
                    <button data-testid={`license-revoke-${l.license_id}`} onClick={() => revokeLicense(l)} className="h-8 px-2 inline-flex items-center gap-1 rounded-md text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950 text-xs font-bold"><Ban className="h-4 w-4" /> Révoquer</button>)}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Créer une quincaillerie" wide>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Nom de la quincaillerie *"><input data-testid="create-store-name" value={createForm.store_name} onChange={setC("store_name")} className={inputCls} /></Field>
          <Field label="Nom du responsable *"><input data-testid="create-owner-name" value={createForm.owner_name} onChange={setC("owner_name")} className={inputCls} /></Field>
          <Field label="Email du compte administrateur *"><input data-testid="create-email" type="email" value={createForm.email} onChange={setC("email")} className={inputCls} /></Field>
          <Field label="Mot de passe *"><input data-testid="create-password" type="text" value={createForm.password} onChange={setC("password")} className={inputCls} placeholder="Min. 6 caractères" /></Field>
          <Field label="Téléphone"><input data-testid="create-phone" value={createForm.phone} onChange={setC("phone")} className={inputCls} /></Field>
          <Field label="Statut initial">
            <select data-testid="create-status" value={createForm.status} onChange={setC("status")} className={selectCls}>
              <option value="active">ACTIVE</option>
              <option value="suspended">SUSPENDUE</option>
              <option value="disabled">DÉSACTIVÉE</option>
            </select>
          </Field>
          <Field label="Ville"><input data-testid="create-city" value={createForm.city} onChange={setC("city")} className={inputCls} /></Field>
          <Field label="Pays"><input data-testid="create-country" value={createForm.country} onChange={setC("country")} className={inputCls} /></Field>
        </div>
        <Field label="Adresse"><input data-testid="create-address" value={createForm.address} onChange={setC("address")} className={inputCls} /></Field>
        <p className="text-xs text-muted-foreground mt-2">Un espace privé complet est créé : compte administrateur, magasin principal et caisse. Quota : 1 administrateur + 3 agents.</p>
        <button data-testid="create-store-submit" onClick={createStore}
          disabled={busy || !createForm.store_name || !createForm.owner_name || !createForm.email || createForm.password.length < 6}
          className={`${btnPrimary} w-full mt-3`}>{busy ? "Création..." : "Créer la quincaillerie"}</button>
      </Modal>

      <Modal open={!!editFor} onClose={() => setEditFor(null)} title={`Modifier — ${editFor?.name}`} wide>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Nom"><input data-testid="edit-name" value={editForm.name || ""} onChange={setE("name")} className={inputCls} /></Field>
          <Field label="Responsable"><input data-testid="edit-owner" value={editForm.owner_name || ""} onChange={setE("owner_name")} className={inputCls} /></Field>
          <Field label="Email"><input data-testid="edit-email" value={editForm.email || ""} onChange={setE("email")} className={inputCls} /></Field>
          <Field label="Téléphone"><input data-testid="edit-phone" value={editForm.phone || ""} onChange={setE("phone")} className={inputCls} /></Field>
          <Field label="Ville"><input data-testid="edit-city" value={editForm.city || ""} onChange={setE("city")} className={inputCls} /></Field>
          <Field label="Pays"><input data-testid="edit-country" value={editForm.country || ""} onChange={setE("country")} className={inputCls} /></Field>
        </div>
        <Field label="Adresse"><input data-testid="edit-address" value={editForm.address || ""} onChange={setE("address")} className={inputCls} /></Field>
        <button data-testid="edit-store-submit" onClick={saveEdit} disabled={busy} className={`${btnPrimary} w-full mt-3`}>{busy ? "Enregistrement..." : "Enregistrer"}</button>
      </Modal>

      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail?.tenant?.name} wide>
        {detail && (
          <div className="space-y-4" data-testid="tenant-detail">
            <div className="flex items-center gap-3 flex-wrap text-sm">
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${(STATUS_META[detail.tenant.status] || STATUS_META.active).cls}`}>
                {(STATUS_META[detail.tenant.status] || STATUS_META.active).label}</span>
              <span className="text-muted-foreground">{detail.tenant.city} · {detail.tenant.email}</span>
              <span className="text-muted-foreground">Dernière connexion : <b data-testid="detail-last-login">{detail.last_login ? fmtDateTime(detail.last_login) : "jamais"}</b></span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-muted rounded-lg p-3 text-center"><div className="text-xs text-muted-foreground">CA total</div><div className="font-money font-bold">{fmtMoney(detail.sales_total)}</div></div>
              <div className="bg-muted rounded-lg p-3 text-center"><div className="text-xs text-muted-foreground">Ventes</div><div className="font-money font-bold">{detail.sales_count}</div></div>
              <div className="bg-muted rounded-lg p-3 text-center"><div className="text-xs text-muted-foreground">Valeur stock</div><div className="font-money font-bold">{fmtMoney(detail.stock_value)}</div></div>
              <div className="bg-muted rounded-lg p-3 text-center"><div className="text-xs text-muted-foreground">Mouvements</div><div className="font-money font-bold">{detail.movements_count}</div></div>
            </div>
            <h3 className="font-heading font-bold text-sm">Utilisateurs ({detail.users.length})</h3>
            <div className="border rounded-lg divide-y max-h-40 overflow-y-auto" data-testid="detail-users">
              {detail.users.map((u) => (
                <div key={u.id} className="flex items-center justify-between px-3 py-2 text-sm">
                  <div><span className="font-semibold">{u.name}</span> <span className="text-xs text-muted-foreground">{u.email}</span></div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-semibold">{u.role}</span>
                    <span className={u.active ? "text-emerald-600" : "text-rose-600"}>{u.active ? "actif" : "désactivé"}</span>
                    {u.last_login_at && <span className="text-muted-foreground">vu {fmtDateTime(u.last_login_at)}</span>}
                  </div>
                </div>
              ))}
            </div>
            <h3 className="font-heading font-bold text-sm">Ventes récentes</h3>
            <div className="border rounded-lg divide-y max-h-40 overflow-y-auto" data-testid="detail-sales">
              {detail.recent_sales.length === 0 && <div className="px-3 py-4 text-sm text-muted-foreground text-center">Aucune vente</div>}
              {detail.recent_sales.map((s) => (
                <div key={s.number + s.created_at} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span className="font-money font-semibold">{s.number} — {s.customer_name}</span>
                  <span className="flex items-center gap-2"><Badge status={s.payment_status} /><span className="font-money font-bold">{fmtMoney(s.total)}</span></span>
                </div>
              ))}
            </div>
            <h3 className="font-heading font-bold text-sm flex items-center gap-1"><Activity className="h-4 w-4" /> Activité récente</h3>
            <div className="border rounded-lg divide-y max-h-48 overflow-y-auto" data-testid="detail-activity">
              {detail.last_activity.map((a) => (
                <div key={a.id} className="px-3 py-2 text-xs flex justify-between gap-2">
                  <span><b>{a.user_name || a.user_email}</b> — {a.action}</span>
                  <span className="text-muted-foreground shrink-0">{fmtDateTime(a.created_at)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!deleteFor} onClose={() => setDeleteFor(null)} title="Supprimer la quincaillerie">
        {deleteFor && (
          <div className="space-y-4" data-testid="delete-modal">
            <div className="flex gap-3 items-start bg-rose-50 dark:bg-rose-950 border border-rose-200 dark:border-rose-900 rounded-lg p-3">
              <AlertTriangle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
              <p className="text-sm text-rose-800 dark:text-rose-200">
                Êtes-vous sûr de vouloir supprimer définitivement cette quincaillerie et toutes ses données ?
                Les utilisateurs seront désactivés et la connexion bloquée. Les données seront conservées
                temporairement pour permettre une restauration.
              </p>
            </div>
            <p className="text-sm">Pour confirmer, saisissez le nom exact de la quincaillerie : <b>{deleteFor.name}</b></p>
            <input data-testid="delete-confirm-name" value={deleteName} onChange={(e) => setDeleteName(e.target.value)}
              className={inputCls} placeholder={deleteFor.name} autoFocus />
            <div className="flex gap-2">
              <button data-testid="delete-cancel" onClick={() => setDeleteFor(null)} className={`${btnSecondary} flex-1`}>Annuler</button>
              <button data-testid="delete-confirm-btn" onClick={confirmDelete}
                disabled={busy || deleteName !== deleteFor.name}
                className="flex-1 h-10 inline-flex items-center justify-center gap-2 rounded-md bg-rose-600 text-white font-bold text-sm hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed">
                <Trash2 className="h-4 w-4" /> {busy ? "Suppression..." : "Supprimer"}</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!purgeFor} onClose={() => setPurgeFor(null)} title="Suppression définitive">
        {purgeFor && (
          <div className="space-y-4" data-testid="purge-modal">
            <div className="flex gap-3 items-start bg-rose-600 text-white rounded-lg p-3">
              <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
              <p className="text-sm font-semibold">
                ACTION IRRÉVERSIBLE : « {purgeFor.name} » et toutes ses données (utilisateurs, produits, stocks,
                ventes, achats, clients, fournisseurs, documents, dettes, dépenses, caisse, historique,
                paramètres) seront définitivement effacées, sans possibilité de restauration.
              </p>
            </div>
            <p className="text-sm">Pour confirmer, saisissez le nom exact de la quincaillerie : <b>{purgeFor.name}</b></p>
            <input data-testid="purge-confirm-name" value={purgeName} onChange={(e) => setPurgeName(e.target.value)}
              className={inputCls} placeholder={purgeFor.name} autoFocus />
            <div className="flex gap-2">
              <button data-testid="purge-cancel" onClick={() => setPurgeFor(null)} className={`${btnSecondary} flex-1`}>Annuler</button>
              <button data-testid="purge-confirm-btn" onClick={confirmPurge}
                disabled={busy || purgeName !== purgeFor.name}
                className="flex-1 h-10 inline-flex items-center justify-center gap-2 rounded-md bg-rose-600 text-white font-bold text-sm hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed">
                <Trash2 className="h-4 w-4" /> {busy ? "Suppression..." : "Supprimer définitivement"}</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!licResult} onClose={() => setLicResult(null)} title="Licence générée">
        {licResult && (
          <div className="space-y-3" data-testid="license-result">
            <p className="text-sm">Expire le <b>{licResult.expires_at}</b> — Installation <code className="font-mono font-bold">{licResult.installation_id}</code></p>
            <Field label="Code d'activation">
              <textarea data-testid="activation-code" readOnly value={licResult.activation_code} className={`${inputCls} h-24 font-mono text-xs`} />
            </Field>
            <div className="flex gap-2">
              <button data-testid="copy-activation-code" onClick={() => { navigator.clipboard.writeText(licResult.activation_code); toast.success("Code copié"); }} className={`${btnSecondary} flex-1`}><Copy className="h-4 w-4" /> Copier le code</button>
              <button data-testid="download-qlic" onClick={() => downloadQlic(licResult.qlic.payload, licResult.qlic.signature)} className={`${btnPrimary} flex-1`}><Download className="h-4 w-4" /> Télécharger license.qlic</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}